import { RichTextInput } from '@/components/RichTextInput/RichTextInput';
import { alert } from '@/services/alert';
import { fetchBot } from '@/services/services';
import { CommunicationThreadTopic, COMMUNICATION_THREAD_TOPIC_PATIENT_LABELS } from '@bariendo/lib/constants';
import { Button, Container, Group, Paper, Radio, Stack, Title } from '@mantine/core';
import { Patient, Reference } from '@medplum/fhirtypes';
import { ResourceInput, useMedplum } from '@medplum/react';
import { ReactElement, useState } from 'react';

export function SimulatePatientChatPage(): ReactElement {
  const medplum = useMedplum();
  const [patient, setPatient] = useState<Reference<Patient> | null>(null);
  const [topic, setTopic] = useState<CommunicationThreadTopic>(CommunicationThreadTopic.LOGISTICAL);
  const [message, setMessage] = useState<string>('');
  const [richTextContent, setRichTextContent] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!patient) {
      alert.error('Select a patient.');
      return;
    }

    if (!message.trim()) {
      alert.error('Add a message.');
      return;
    }

    try {
      setIsSubmitting(true);

      await fetchBot(medplum, 'simulate-patient-chat-message', {
        patientRef: patient,
        topic,
        message,
        richTextContent: JSON.stringify(richTextContent),
      });

      alert.success('Patient chat message sent.');

      // Reset form
      setMessage('');
      setRichTextContent({});
    } catch (error) {
      alert.error(`Couldn't send patient chat message: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container size="sm" py="xl">
      <Paper p="lg" shadow="sm">
        <Title order={2} mb="lg">
          Simulate patient chat
        </Title>

        <Stack gap="md">
          <ResourceInput
            name="patient"
            resourceType="Patient"
            label="Patient"
            required
            onChange={(value) => {
              setPatient(value as Reference<Patient>);
            }}
          />

          <Radio.Group
            label="Topic"
            value={topic}
            onChange={(value) => setTopic(value as CommunicationThreadTopic)}
            required
          >
            <Group mt="xs">
              <Radio
                value={CommunicationThreadTopic.LOGISTICAL}
                label={COMMUNICATION_THREAD_TOPIC_PATIENT_LABELS[CommunicationThreadTopic.LOGISTICAL]}
              />
              <Radio
                value={CommunicationThreadTopic.CLINICAL}
                label={COMMUNICATION_THREAD_TOPIC_PATIENT_LABELS[CommunicationThreadTopic.CLINICAL]}
              />
              <Radio
                value={CommunicationThreadTopic.NUTRITION}
                label={COMMUNICATION_THREAD_TOPIC_PATIENT_LABELS[CommunicationThreadTopic.NUTRITION]}
              />
            </Group>
          </Radio.Group>

          <RichTextInput
            value={richTextContent}
            onChange={(text, json) => {
              setMessage(text);
              setRichTextContent(json);
            }}
            placeholder="Write a message..."
          />

          <Button onClick={handleSubmit} loading={isSubmitting} disabled={!patient || !message.trim()} fullWidth>
            Send message
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}
